import {
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { FieldArray, Form, Formik } from "formik";
import * as Yup from "yup";
import Input from "../../../../component-lib/Atoms/Input/Input";
import Button from "../../../../component-lib/Molecules/button/Button";
import SelectDropdown from "../../../../component-lib/Atoms/Select/Select";
import BootstrapDialog from "../../../../utils/bootstrap-dialog-style/BootstrapDialog";
import { TEXT_CONSTANTS } from "../../../../utils/text-constants/textConstants";
import {
  ADD_INVENTORY_NOTES_DATA,
  INVENTORY_NOTE_TYPE_OPTIONS,
} from "./adjustInventoryConstants";

interface NoteRow {
  type: string;
  quantity: string;
  comment: string;
}

interface AdjustInventoryDialogProps {
  isOpen: boolean;
  dialogHeading?: string;
  defaultDataForInventory?: any;
  handleClose: () => void;
  handleData: (data: any) => void;
}

const validationSchema = Yup.object().shape({
  [ADD_INVENTORY_NOTES_DATA.NOTES]: Yup.array()
    .of(
      Yup.object().shape({
        [ADD_INVENTORY_NOTES_DATA.NOTE_TYPE]: Yup.string().required(
          "Note type is required"
        ),
        [ADD_INVENTORY_NOTES_DATA.QUANTITY]: Yup.string()
          .matches(/^\d+$/, "Quantity must be a number")
          .required("Quantity is required"),
        [ADD_INVENTORY_NOTES_DATA.COMMENT]: Yup.string(),
      })
    )
    .min(1, "At least one note is required"),
});

export default function AdjustInventoryDialog({
  isOpen = false,
  dialogHeading = "Adjust Inventory",
  defaultDataForInventory,
  handleClose,
  handleData,
}: AdjustInventoryDialogProps) {
  const initialValues: {
    notes: NoteRow[];
  } = {
    notes: [{ type: "", quantity: "", comment: "" }],
  };

  return (
    <BootstrapDialog
      onClose={(event, reason) => {
        if (reason !== "backdropClick") {
          handleClose();
        }
      }}
      aria-labelledby="adjust-inventory-dialog-title"
      open={isOpen}
      customWidth="900px"
    >
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={(values, { setSubmitting }) => {
          try {
            handleData(values);
          } catch (error) {
            console.log(error);
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({ errors, values, touched, setFieldValue, handleBlur, handleSubmit }) => {
          return (
            <Form onSubmit={handleSubmit}>
              <DialogTitle
                sx={{
                  m: 0,
                  p: 2,
                  fontWeight: 600,
                  fontSize: "18px",
                  fontFamily: "inter",
                }}
                id="adjust-inventory-dialog-title"
              >
                {dialogHeading}
                {defaultDataForInventory?.itemName
                  ? ` - ${defaultDataForInventory?.itemName}`
                  : ""}
              </DialogTitle>
              <IconButton
                aria-label="close"
                onClick={handleClose}
                sx={(theme) => ({
                  position: "absolute",
                  right: 8,
                  top: 8,
                  color: theme.palette.grey[500],
                })}
              >
                <CloseIcon />
              </IconButton>
              <hr />

              <DialogContent>
                <FieldArray name={ADD_INVENTORY_NOTES_DATA.NOTES}>
                  {({ push, remove }) => (
                    <div className="flex flex-col gap-4 mt-4">
                      {values.notes.map((note, index) => {
                        const noteErrors: any =
                          (errors.notes as any)?.[index] || {};
                        const noteTouched: any =
                          (touched.notes as any)?.[index] || {};
                        // Types chosen in OTHER rows are removed so each type is used once.
                        const selectedTypesInOtherRows = values.notes
                          .filter((_, i) => i !== index)
                          .map((n) => n.type)
                          .filter(Boolean);
                        const availableTypeOptions =
                          INVENTORY_NOTE_TYPE_OPTIONS.filter(
                            (opt) =>
                              opt.value === note.type ||
                              !selectedTypesInOtherRows.includes(opt.value)
                          );
                        return (
                          <div
                            key={index}
                            className="flex items-start gap-3 w-full"
                          >
                            <div className="flex-1">
                              <SelectDropdown
                                displayEmpty
                                placeholderValue="Select note type"
                                name={`notes.${index}.type`}
                                value={note.type}
                                options={availableTypeOptions}
                                onChangeCb={(e) =>
                                  setFieldValue(
                                    `notes.${index}.type`,
                                    e.target.value
                                  )
                                }
                                onBlur={handleBlur}
                                error={
                                  noteTouched?.type && Boolean(noteErrors?.type)
                                }
                                helperText={
                                  noteTouched?.type ? noteErrors?.type : undefined
                                }
                              />
                            </div>
                            <div className="w-[120px]">
                              <Input
                                textFieldLabel="Quantity"
                                type="number"
                                size="small"
                                name={`notes.${index}.quantity`}
                                value={note.quantity}
                                onChangeCb={(e) =>
                                  setFieldValue(
                                    `notes.${index}.quantity`,
                                    e.target.value
                                  )
                                }
                                onBlur={handleBlur}
                                error={
                                  noteTouched?.quantity &&
                                  Boolean(noteErrors?.quantity)
                                }
                                helperText={
                                  noteTouched?.quantity
                                    ? noteErrors?.quantity
                                    : undefined
                                }
                              />
                            </div>
                            <div className="flex-1">
                              <Input
                                size="small"
                                placeholder="Note"
                                textFieldLabel="Note"
                                name={`notes.${index}.comment`}
                                value={note.comment}
                                onChangeCb={(e) =>
                                  setFieldValue(
                                    `notes.${index}.comment`,
                                    e.target.value
                                  )
                                }
                                onBlur={handleBlur}
                              />
                            </div>
                            <IconButton
                              aria-label="remove-note"
                              onClick={() => remove(index)}
                              disabled={values.notes.length === 1}
                              sx={{
                                marginTop: "4px",
                                color:
                                  values.notes.length === 1
                                    ? "#CCCCCC"
                                    : "#CE0718",
                              }}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </div>
                        );
                      })}

                      <div className="flex justify-end">
                        <Button
                          type="button"
                          label="+ Add More"
                          variant="text"
                          disabled={
                            values.notes.length >=
                            INVENTORY_NOTE_TYPE_OPTIONS.length
                          }
                          onClickCb={() =>
                            push({ type: "", quantity: "", comment: "" })
                          }
                          style={{ padding: "4px 8px", fontSize: "14px" }}
                        />
                      </div>
                    </div>
                  )}
                </FieldArray>
              </DialogContent>

              <DialogActions style={{ padding: "24px" }}>
                <Button
                  label={TEXT_CONSTANTS?.CANCEL}
                  variant="outlined"
                  onClickCb={() => handleClose()}
                  style={{
                    width: "120px",
                    height: "44px",
                    padding: "12px 20px",
                    fontSize: "16px",
                    backgroundColor: "white",
                    border: "none",
                    color: "#393939",
                  }}
                />
                <Button
                  type="submit"
                  label="Adjust"
                  variant="contained"
                  onClickCb={() => {}}
                  style={{ padding: "8px 55px", fontSize: "16px" }}
                />
              </DialogActions>
            </Form>
          );
        }}
      </Formik>
    </BootstrapDialog>
  );
}
